import * as React from "react";
import {
    CheckmarkCircleFilled,
    DismissCircleFilled,
    DismissRegular,
    PlayCircleRegular,
    ClockRegular,
    BranchRegular,
    NumberSymbolSquareRegular,
} from "@fluentui/react-icons";
import { useHealReportViewStyles } from "../styles/healReportView.styles";
import type { ProjectVideoFile, SessionResponse } from "../api/models/ApiModels";
import { TestResultsModel, type TestCase } from "../models/TestResultModels";
import { findRecordingForVideoPath, mapRecordingsToVideoFiles } from "../utils/recordings";
import { useVideoPlayer } from "../utils/useVideoPlayer";
import { fetchJsonWithRetry } from "../utils/fetchJson";
import { InfoRow, Section, SummaryStat, AttributionTag, StatusIcon } from "./reportShared";

/* ── Scout report JSON schema ── */

interface ConsideredTest {
    name: string;
    reason: string;
}

interface ScoutTest {
    /** Spec file path, exactly as Playwright reports it (relative to rootDir). */
    file: string;
    testName: string;
    description?: string | null;
    rationale?: string | null;
    /** Attribution: covers the change's own behavior, or an adjacent pre-existing gap. */
    origin?: "change" | "adjacent" | null;
}

interface ScoutAppDefect {
    testName: string;
    description: string;
    origin?: "pr_related" | "pre_existing" | "unknown" | null;
    evidence?: string | null;
}

interface CoverageGap {
    description: string;
    reason: string;
}

interface RetiredTest {
    file?: string | null;
    testName: string;
    reason: string;
}

interface ScoutReport {
    metadata: {
        timestamp: string;
        branch: string;
        commitSha: string;
        prNumber?: number | null;
        gitRemoteUrl?: string | null;
        commitUrl?: string | null;
        branchUrl?: string | null;
        prUrl?: string | null;
        userInstruction?: string | null;
        /** testDir relative to the repo root, set by the CLI; joined with `file`
         *  to build repo-root links. */
        repoTestRoot?: string | null;
    };
    /** Run scope — did this scout target a specific change's blast radius, or the whole app. */
    scoped?: boolean;
    /** Area 1 — verification: is the app correct? */
    verification?: {
        verdict?: "works" | "issues_found" | "not_verified" | null;
        text?: string | null;
        appDefects?: ScoutAppDefect[];
        /** Legacy key from reports written before the rename; read via `appDefects ?? bugs`. */
        bugs?: ScoutAppDefect[];
    };
    plan: {
        consideredTests?: ConsideredTest[];
    };
    tests: ScoutTest[];
    /** Area 2 — coverage gaps consciously left unactioned. */
    coverageGaps?: CoverageGap[];
    /** Area 2 — existing tests removed/consolidated as maintenance. */
    retired?: RetiredTest[];
    summary: {
        text: string;
    };
}

interface ScoutReportViewProps {
    session: SessionResponse;
    onClose: () => void;
    projectId?: string;
}

/* ── Helpers ── */

function verdictColor(verdict?: string | null): string {
    if (verdict === "issues_found") return "#f85149";
    if (verdict === "works") return "#3fb950";
    return "#8b949e";
}

/** Top-line verdict phrase — grey text; the colored status icon carries the pass/fail cue. */
function verdictPhrase(verdict?: string | null): string {
    if (verdict === "works") return "Verified live";
    if (verdict === "issues_found") return "Broke live";
    if (verdict === "not_verified") return "Couldn't verify";
    return "";
}

/** Attribution phrasing — a test covers the change vs an adjacent gap. */
function testOriginLabel(origin?: string | null): string | null {
    if (origin === "change") return "Covers this change's new behavior";
    if (origin === "adjacent") return "Adjacent — a pre-existing gap on a surface this change touches";
    return null;
}

/** Attribution phrasing for an app defect — same vocabulary heal uses for its findings. */
function appDefectOriginLabel(origin?: string | null): string | null {
    if (origin === "pr_related") return "Introduced by this change";
    if (origin === "pre_existing") return "Pre-existing — not introduced by this change";
    return null;
}

function testTitle(name: string): string {
    const parts = name.split(" > ");
    return parts[parts.length - 1] ?? name;
}

function testGroup(name: string): string {
    const parts = name.split(" > ");
    return parts.length > 1 ? parts.slice(0, -1).join(" > ") : "";
}

function displayFileName(filePath: string): string {
    return filePath.split("/").pop() ?? filePath;
}

function normalizeAdoUrl(url: string | null | undefined): string | null | undefined {
    if (!url) return url;
    return url.replace(/\/(DefaultCollection)\//i, "/");
}

/** Join the report's `repoTestRoot` prefix with a test's `file` (relative to
 *  rootDir) to form the repo-root path used in source links. Null when no prefix
 *  is known (file then isn't safely linkable). */
function repoFilePath(repoTestRoot: string | null | undefined, file: string | null | undefined): string | null {
    if (!file || repoTestRoot == null) return null;
    const f = file.replace(/\\/g, "/").replace(/^\/+/, "");
    const prefix = repoTestRoot.replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
    return prefix ? `${prefix}/${f}` : f;
}

function buildFileUrl(gitRemoteUrl: string | null | undefined, commitSha: string | null | undefined, repoPath: string | null | undefined): string | null {
    if (!gitRemoteUrl || !commitSha || !repoPath) return null;
    const remote = normalizeAdoUrl(gitRemoteUrl.replace(/\/$/, ""))!;
    const path = `/${repoPath.replace(/^\/+/, "")}`;
    if (remote.includes("github.com")) {
        return `${remote}/blob/${commitSha}${path}`;
    }
    if (remote.includes("dev.azure.com") || remote.includes("visualstudio.com")) {
        return `${remote}?path=${path}&version=GC${commitSha}`;
    }
    return null;
}

/* ── Recording helpers ──
 *
 * Scout uses the EXACT SAME PIPELINE as the run view (ProjectDashboard):
 *   1. Parse test-results.json via TestResultsModel.fromRaw → TestCase[]
 *   2. Convert session.recordings → ProjectVideoFile[] via mapRecordingsToVideoFiles
 *   3. Match TestCase.primaryVideoPath → recording via findRecordingForVideoPath
 *
 * The only scout-specific bit is the join from a scout-report `tests[]`
 * entry (LLM-written, has description/rationale) → its matching TestCase
 * (Playwright-written, has the attachment path). We join by file basename
 * + leaf title. Everything past that point is identical to run. */

/* ── Component ── */

const ScoutReportView: React.FC<ScoutReportViewProps> = ({ session, onClose, projectId }) => {
    const s = useHealReportViewStyles();
    const [report, setReport] = React.useState<ScoutReport | null>(null);
    const [testResults, setTestResults] = React.useState<TestResultsModel | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const { playRecording, modal: videoModal, isPlaying, close: closeVideo } = useVideoPlayer(projectId);

    // Same shape `ProjectDashboard` (run view) uses — single source of truth.
    const videoFiles: ProjectVideoFile[] = React.useMemo(
        () => mapRecordingsToVideoFiles(session.recordings),
        [session]
    );

    const reportSasUrl = React.useMemo(
        () => session.files?.find((f) => f.fileType.trim().toLowerCase() === "scout-report" && !!f.sasUri)?.sasUri ?? null,
        [session]
    );

    // Playwright's own test-results.json — uploaded by every scout/heal/run
    // session. Same file the run view consumes; we parse it with the same
    // TestResultsModel.fromRaw so the test → video attachment mapping is
    // identical to what TestResultsTable renders for run.
    const pwReportSasUrl = React.useMemo(
        () => session.files?.find((f) => f.fileType.trim().toLowerCase() === "test-results" && !!f.sasUri)?.sasUri ?? null,
        [session]
    );

    React.useEffect(() => {
        if (!reportSasUrl) { setLoading(false); setError("No scout report data available for this session."); return; }
        const ctrl = new AbortController();
        const fetchUrl = import.meta.env.DEV ? `/blob-proxy?url=${encodeURIComponent(reportSasUrl)}` : reportSasUrl;
        fetchJsonWithRetry<ScoutReport>(fetchUrl, ctrl.signal)
            .then((data) => { setReport(data); setLoading(false); })
            .catch((err) => {
                if (ctrl.signal.aborted) return;
                console.error("[ScoutReportView] scout-report.json fetch failed", err);
                setError(`Failed to load report: ${err.message}`);
                setLoading(false);
            });
        return () => ctrl.abort();
    }, [reportSasUrl]);

    // Best-effort: failure to load test-results.json just means no videos.
    React.useEffect(() => {
        if (!pwReportSasUrl) { setTestResults(null); return; }
        const ctrl = new AbortController();
        const fetchUrl = import.meta.env.DEV ? `/blob-proxy?url=${encodeURIComponent(pwReportSasUrl)}` : pwReportSasUrl;
        fetchJsonWithRetry<Parameters<typeof TestResultsModel.fromRaw>[0]>(fetchUrl, ctrl.signal)
            .then((data) => { if (data) setTestResults(TestResultsModel.fromRaw(data)); })
            .catch((err) => {
                if (ctrl.signal.aborted) return;
                console.error("[ScoutReportView] test-results.json fetch failed", err);
                setTestResults(null);
            });
        return () => ctrl.abort();
    }, [pwReportSasUrl]);

    /** Join a scout-report test entry to the matching Playwright TestCase by file
     *  basename + leaf title (case-insensitive). */
    const findTestCaseForScout = React.useCallback((test: ScoutTest): TestCase | undefined => {
        if (!testResults) return undefined;
        const all = testResults.allTestCases;
        const leafTitle = testTitle(test.testName).toLowerCase();
        const candidateBase = (test.file.replace(/\\/g, "/").split("/").pop() ?? "").toLowerCase();
        return all.find((tc) => {
            const tcBase = (tc.file.replace(/\\/g, "/").split("/").pop() ?? "").toLowerCase();
            if (tcBase !== candidateBase) return false;
            const tcTitle = tc.title.toLowerCase();
            return tcTitle === leafTitle || tcTitle === test.testName.toLowerCase();
        });
    }, [testResults]);

    /** Identical pipeline to ProjectDashboard.handleWatchVideo (run view):
     *  TestCase.primaryVideoPath → findRecordingForVideoPath → ProjectVideoFile. */
    const resolveScoutRecording = React.useCallback((test: ScoutTest): ProjectVideoFile | undefined => {
        const tc = findTestCaseForScout(test);
        return findRecordingForVideoPath(tc?.primaryVideoPath, videoFiles);
    }, [findTestCaseForScout, videoFiles]);

    React.useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            if (isPlaying) closeVideo();
            else onClose();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose, isPlaying, closeVideo]);

    const meta = report?.metadata;
    const commitUrl = normalizeAdoUrl(meta?.commitUrl);
    const branchUrl = normalizeAdoUrl(meta?.branchUrl);
    const prUrl = normalizeAdoUrl(meta?.prUrl);
    const timestamp = meta?.timestamp ?? session.completedDateTime ?? session.createdDateTime;
    const formattedTime = timestamp
        ? new Date(timestamp).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
        : "";

    const addedTests = report?.tests ?? [];

    const consideredTests: ConsideredTest[] = report?.plan.consideredTests ?? [];

    // `appDefects` is the current key; `bugs` is the legacy key from reports written before the
    // rename — read both so already-published scout reports keep rendering their app defects.
    const scoutDefects = report?.verification?.appDefects ?? report?.verification?.bugs ?? [];

    return (
        <div className={s.overlay}>
            {/* Header */}
            <div className={s.header}>
                <div className={s.headerLeft}>
                    <img src="/icons/app-icon.svg" alt="" className={s.headerLogo} />
                    <span className={s.headerTitleGroup}>
                        <span className={s.headerTitle}>Scout Report</span>
                        <span className={s.badgeScout}>SCOUT</span>
                    </span>
                    {formattedTime && (
                        <span className={s.headerMetaItem}>
                            <span className={s.headerMetaIcon}><ClockRegular fontSize={16} /></span>
                            <span className={s.headerMeta}>{formattedTime}</span>
                        </span>
                    )}
                    {meta?.branch && (
                        <span className={s.headerMetaItem}>
                            <span className={s.headerMetaIcon}><BranchRegular fontSize={16} /></span>
                            {branchUrl
                                ? <a href={branchUrl} target="_blank" rel="noopener noreferrer" className={s.headerMetaLink}>{meta.branch}</a>
                                : <span className={s.headerMeta}>{meta.branch}</span>}
                        </span>
                    )}
                    {meta?.commitSha && (
                        <span className={s.headerMetaItem}>
                            <span className={s.headerMetaIcon}><NumberSymbolSquareRegular fontSize={16} /></span>
                            {commitUrl
                                ? <a href={commitUrl} target="_blank" rel="noopener noreferrer" className={s.headerCommit}>{meta.commitSha.substring(0, 7)}</a>
                                : <span className={s.headerCommit}>{meta.commitSha.substring(0, 7)}</span>}
                        </span>
                    )}
                    {meta?.prNumber && (
                        prUrl
                            ? <a href={prUrl} target="_blank" rel="noopener noreferrer" className={s.headerPr}>PR #{meta.prNumber}</a>
                            : <span className={s.headerPr}>PR #{meta.prNumber}</span>
                    )}
                </div>
                <button className={s.closeBtn} onClick={onClose} aria-label="Close"><DismissRegular /></button>
            </div>

            {/* Body */}
            <div className={s.body}>
                <div className={s.reportPane}>
                    <div className={s.reportContent}>
                        {loading && <div className={s.loadingCenter}>Loading scout report…</div>}
                        {error && <div className={s.errorCenter}>{error}</div>}

                        {report && (
                            <>
                                {/* Summary banner — two areas */}
                                <div className={s.summaryBanner}>
                                    {(report.scoped !== undefined || report.verification?.verdict) && (
                                        <div className={s.scopeLine}>
                                            {report.scoped !== undefined && (
                                                <span>{report.scoped ? "Scoped to the change" : "Whole-app scan"}</span>
                                            )}
                                            {report.scoped !== undefined && verdictPhrase(report.verification?.verdict) && <span aria-hidden="true">·</span>}
                                            {verdictPhrase(report.verification?.verdict) && (
                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                                    {report.verification!.verdict === "works" && <CheckmarkCircleFilled style={{ color: "#3fb950", fontSize: 13 }} />}
                                                    {report.verification!.verdict === "issues_found" && <DismissCircleFilled style={{ color: "#f85149", fontSize: 13 }} />}
                                                    <span>{verdictPhrase(report.verification!.verdict)}</span>
                                                </span>
                                            )}
                                            {scoutDefects.length > 0 && (
                                                <>
                                                    <span aria-hidden="true">·</span>
                                                    <span><span style={{ fontWeight: 600, color: "#f85149" }}>{scoutDefects.length}</span> {scoutDefects.length === 1 ? "app defect" : "app defects"}</span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {/* Area 2 — Coverage */}
                                    {(() => {
                                        const stats = [
                                            { count: report.tests.length, label: "Coverage added", color: "#3fb950" },
                                            { count: report.coverageGaps?.length ?? 0, label: "Coverage gaps", color: "#d29922" },
                                            { count: report.retired?.length ?? 0, label: "Retired", color: "#8b949e" },
                                        ].filter((st) => st.count > 0);
                                        return stats.length > 0 ? (
                                            <div className={s.summaryStats}>
                                                {stats.map((st, i) => (
                                                    <React.Fragment key={st.label}>
                                                        {i > 0 && <span className={s.summaryStatDivider} aria-hidden="true" />}
                                                        <SummaryStat count={st.count} label={st.label} color={st.color} />
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        ) : null;
                                    })()}
                                    <div className={s.summaryLabel}>Session summary</div>
                                    <div className={s.summaryText}>{report.summary.text}</div>
                                </div>

                                {/* Verification — is the app correct? (the verified-live narrative) */}
                                {report.verification && report.verification.text && (
                                    <Section title="Verification" color={verdictColor(report.verification.verdict)} defaultOpen>
                                        <div className={s.testCard} style={{ borderLeftColor: verdictColor(report.verification.verdict) }}>
                                            <div>
                                                <div className={s.summaryLabel}>{report.verification.verdict === "not_verified" ? "Couldn't verify live" : report.verification.verdict === "works" ? "Verified live" : "Live verification"}</div>
                                                <div className={s.summaryText}>{report.verification.text}</div>
                                            </div>
                                        </div>
                                    </Section>
                                )}

                                {/* App defects — the app is broken, not the test; same section name + 🐛 heal uses */}
                                {scoutDefects.length > 0 && (
                                    <Section title={`App defects (${scoutDefects.length})`} color="#f85149" defaultOpen>
                                        {scoutDefects.map((b, i) => (
                                            <div key={i} className={s.testCard} style={{ borderLeftColor: "#f85149" }}>
                                                <div className={s.testCardTopRow}>
                                                    <div className={s.testCardLeft}>
                                                        <div className={s.testCardContent}>
                                                            <div className={s.testCardName} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                                                <span className={s.testCardStatusIcon} style={{ color: "#f85149" }}><DismissCircleFilled /></span>
                                                                <span>{testTitle(b.testName)}</span>
                                                                {b.origin && <AttributionTag origin={b.origin} title={appDefectOriginLabel(b.origin) ?? undefined} />}
                                                            </div>
                                                            {testGroup(b.testName) && (
                                                                <div className={s.testCardMeta} style={{ paddingLeft: 24 }}>
                                                                    <span className={s.testCardMetaPath}>{testGroup(b.testName)}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className={s.testCardBody}>
                                                    <InfoRow label="Issue" text={b.description} status="appbug" />
                                                    {b.evidence && <InfoRow label="Evidence" text={b.evidence} status="appbug" />}
                                                </div>
                                            </div>
                                        ))}
                                    </Section>
                                )}

                                {/* New Tests Added */}
                                {report.tests.length > 0 && (
                                    <Section title={`Coverage added (${report.tests.length})`} color="#3fb950" defaultOpen>
                                        {addedTests.map((t, i) => {
                                            const rec = resolveScoutRecording(t);
                                            const fileUrl = buildFileUrl(meta?.gitRemoteUrl, meta?.commitSha, repoFilePath(meta?.repoTestRoot, t.file));
                                            const displayFile = t.file;
                                            return (
                                                <div key={i} className={s.testCard} style={{ borderLeftColor: "#3fb950" }}>
                                                    <div className={s.testCardTopRow}>
                                                        <div className={s.testCardLeft}>
                                                            <div className={s.testCardContent}>
                                                                <div className={s.testCardName} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                                                    <span className={s.testCardStatusIcon} style={{ color: "#3fb950" }}><CheckmarkCircleFilled /></span>
                                                                    <span>{testTitle(t.testName)}</span>
                                                                    {t.origin && <AttributionTag origin={t.origin} title={testOriginLabel(t.origin) ?? undefined} />}
                                                                </div>
                                                                <div className={s.testCardMeta} style={{ paddingLeft: 24 }}>
                                                                    {fileUrl
                                                                        ? <a href={fileUrl} target="_blank" rel="noopener noreferrer" className={s.testCardMetaPath} title={displayFile}>{displayFileName(displayFile)}</a>
                                                                        : <span className={s.testCardMetaPath} title={displayFile}>{displayFileName(displayFile)}</span>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className={s.testCardRight}>
                                                            {rec?.url && (
                                                                <button
                                                                    onClick={() => playRecording(rec, testTitle(t.testName))}
                                                                    className={s.actionBtn}
                                                                >
                                                                    <PlayCircleRegular style={{ fontSize: 14 }} />
                                                                    Watch test
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {(t.description || t.rationale) && (
                                                        <div className={s.testCardBody}>
                                                            {t.description && <InfoRow label="Covers" text={t.description} status="fixed" />}
                                                            {t.rationale && <InfoRow label="Why added" text={t.rationale} status="unclassified" />}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </Section>
                                )}

                                {/* Retired — existing tests removed as maintenance */}
                                {(report.retired?.length ?? 0) > 0 && (
                                    <Section title={`Retired (${report.retired!.length})`} color="#8b949e" defaultOpen>
                                        {report.retired!.map((r, i) => (
                                            <div key={i} className={s.testCard} style={{ borderLeftColor: "#8b949e" }}>
                                                <div className={s.testCardContent}>
                                                    <div className={s.testCardName} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                                        <StatusIcon status="removed" color="#8b949e" />
                                                        <span>{testTitle(r.testName)}</span>
                                                    </div>
                                                    {(r.file || testGroup(r.testName)) && (
                                                        <div className={s.testCardMeta} style={{ paddingLeft: 24 }}><span className={s.testCardMetaPath} title={r.file ?? undefined}>{r.file ? displayFileName(r.file) : testGroup(r.testName)}</span></div>
                                                    )}
                                                </div>
                                                <InfoRow label="Why retired" text={r.reason} status="removed" />
                                            </div>
                                        ))}
                                    </Section>
                                )}

                                {/* Coverage gaps not actioned */}
                                {(report.coverageGaps?.length ?? 0) > 0 && (
                                    <Section title={`Coverage gaps (${report.coverageGaps!.length})`} color="#d29922" defaultOpen>
                                        {report.coverageGaps!.map((g, i) => (
                                            <div key={i} className={s.testCard} style={{ borderLeftColor: "#d29922" }}>
                                                <div className={s.testCardName}>{g.description}</div>
                                                <InfoRow label="Why" text={g.reason} status="flaky" />
                                            </div>
                                        ))}
                                    </Section>
                                )}

                                {/* Considered But Not Added */}
                                {consideredTests.length > 0 && (
                                    <Section title={`Not added (${consideredTests.length})`} color="#8b949e" defaultOpen={false}>
                                        {consideredTests.map((ct, i) => (
                                            <div key={i} className={s.testCard} style={{ borderLeftColor: "#8b949e" }}>
                                                {ct.name && ct.name !== `Candidate ${i + 1}` && (
                                                    <div className={s.testCardName}>{ct.name}</div>
                                                )}
                                                <InfoRow label="Why not" text={ct.reason} status="unclassified" />
                                            </div>
                                        ))}
                                    </Section>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {videoModal}
        </div>
    );
};

export default ScoutReportView;
